import { action, makeAutoObservable, observable, when } from 'mobx';
import { chains as allChains } from '../configs';
import Wallets from './Connectors';
import ConnectModalStore from './Modal';
import { isMobile } from './utils';

const LAST_WALLET_KEY = 'mayan-explorer-last-wallet';

/**
 * Lightweight "configs" adapter that satisfies what the wallet stores
 * expect from the monorepo Configs singleton, using the explorer's
 * hardcoded chain list.
 */
class ExplorerConfigs {
	readonly evmChains: Chain[];
	readonly svmChains: Chain[];
	readonly combinedChains: Chain[];
	readonly transformedChains: Chain[];
	readonly appIdentity: { name: string; icon: string };
	readonly filters: Record<string, number[] | undefined> = {};

	constructor() {
		this.combinedChains = allChains;
		this.transformedChains = allChains;
		this.evmChains = allChains.filter((c) => c.mode === 'EVM');
		this.svmChains = allChains.filter((c) => c.mode === 'SOLANA');
		this.appIdentity = { name: 'Mayan Explorer', icon: '/assets/Logo.svg' };
	}
}

/**
 * Lightweight "chains" adapter that satisfies what the wallet stores
 * expect from the monorepo Chains singleton.
 */
class ExplorerChains {
	originChain: Chain;
	destinationChain: Chain;
	readonly originEvmChains: Chain[];

	constructor() {
		const evmChains = allChains.filter((c) => c.mode === 'EVM');
		const solChains = allChains.filter((c) => c.mode === 'SOLANA');
		this.originChain = evmChains[0];
		this.destinationChain = solChains[0] || evmChains[1] || evmChains[0];
		this.originEvmChains = evmChains;

		makeAutoObservable(this, {
			originChain: observable.ref,
			destinationChain: observable.ref,
		});
	}

	setOriginChain = action((chainOrId: Chain | number | undefined) => {
		if (!chainOrId) return;
		if (typeof chainOrId === 'number') {
			const found = allChains.find((c) => c.chainId === chainOrId);
			if (found) this.originChain = found;
		} else {
			this.originChain = chainOrId;
		}
	});

	setDestinationChain = action((chainOrId: Chain | number | undefined) => {
		if (!chainOrId) return;
		if (typeof chainOrId === 'number') {
			const found = allChains.find((c) => c.chainId === chainOrId);
			if (found) this.destinationChain = found;
		} else {
			this.destinationChain = chainOrId;
		}
	});
}

export default class ExplorerRootStore {
	static #instance: ExplorerRootStore;
	static get instance() {
		if (!this.#instance) {
			this.#instance = new ExplorerRootStore();
		}
		return this.#instance;
	}

	configs: ExplorerConfigs;
	chains: ExplorerChains;
	wallets: Wallets;
	modal: ConnectModalStore;

	constructor() {
		this.configs = new ExplorerConfigs();
		this.chains = new ExplorerChains();
		this.wallets = new Wallets(this);
		this.modal = new ConnectModalStore(this);
		this.#autoConnect();
		this.#persistLastWallet();
	}

	#autoConnect() {
		// Restore last connected wallet on page load
		try {
			const raw = localStorage.getItem(LAST_WALLET_KEY);
			const lastUsed = raw ? JSON.parse(raw) : null;
			// Delay to let wallet extensions inject providers
			when(() => true, () => {
				this.wallets.activateFirst(lastUsed);
			});
		} catch {
			when(() => true, () => {
				this.wallets.activateFirst();
			});
		}
	}

	#persistLastWallet() {
		// Save the active origin wallet type+name whenever it changes
		import('mobx').then(({ reaction }) => {
			reaction(
				() => {
					const w = this.wallets.originWallet;
					return w?.isReady ? [w.type, w.name] : null;
				},
				(value) => {
					try {
						if (value) {
							localStorage.setItem(LAST_WALLET_KEY, JSON.stringify(value));
						} else {
							localStorage.removeItem(LAST_WALLET_KEY);
						}
					} catch { /* ignore */ }
				},
			);
		});
	}

	get singleModal() {
		return isMobile();
	}

	get isWebView() {
		return false;
	}
}
